import type { BIAIRequest } from '@/entities/bi'
import type { FilterSchema } from '@/shared/components/filter/validation'
import type { CategoryModel } from '@/shared/generated'
import type { TFunction } from 'i18next'
import type { WidgetPeriods } from '../types'

import React, { useMemo } from 'react'
import { useWidgetParams } from '@/pages/dashboard/lib/useWidgetParams'
import { useWidgetTable } from '@/pages/dashboard/lib/useWidgetTable'
import { filter, find, last, map, prop, uniqBy } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useAiBIAnalyticQuery, useMetricsQuery, zipSeries } from '@/entities/bi'
import { useCategoriesQuery } from '@/entities/core'

import {
	createSRTColumnHelper,
	MasterTable,
	SRT_TableToolbar,
} from '@/shared/components/master-table'
import { ampli } from '@/shared/lib/ampli'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { AiDashboardFilter } from '../../Filter'

const getRowId = prop('category.id')
type DataRow = {
	category_ids: string[]
	price_feature: number
	product_feature: number
	support_feature: number
	trend_feature: number
	season_featur: number
}
export default function SalesFactorsImpactByCategoriesWidget({
	commonFilters,
	commonPeriods,
}: {
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const { params, onChangeParams } = useWidgetParams()
	const { data: metrics, isLoading } = useMetricsQuery<string[]>({
		placeholderData: [],
		select: (data) => map('name', filter({ type: 'ai_importance' }, data)),
	})
	const commonQueryParams = {
		label: 'SalesFactorsImpactByCategories',
		...(commonPeriods?.main || {
			start_date: null,
			end_date: null,
		}),
		skip_dates: true,
		metrics: metrics!,
		dimensions: ['category_ids'] as BIAIRequest['dimensions'],
		date_aggregation: null,
		filters: [...commonFilters, ...(params.filters ?? [])],
	}
	const biQuery = useAiBIAnalyticQuery<DataRow[]>(commonQueryParams, {
		enabled: !!metrics?.length,
		select: (data) => zipSeries<DataRow>(data?.meta, data?.data),
	})
	const categoriesQuery = useCategoriesQuery<CategoryModel[]>(
		{},
		{
			select: uniqBy<CategoryModel>('id'),
		}
	)
	const handleFilterChange = (filters: FilterSchema[]) => {
		ampli.dashboardBiApplyFilterSalesFactorsImpactByCategories({})
		onChangeParams({ ...params, filters })
	}
	const rows = (
		biQuery.data?.map((data) => ({
			category: find({ id: last(data.category_ids) }, categoriesQuery.data)!,
			metrics: data as any as Record<string, number>,
		})) ?? []
	).filter(prop('category'))
	const columns = useMemo(
		() =>
			getColumns(
				{
					metrics: metrics || [],
				},
				t
			),
		[metrics]
	)
	const table = useWidgetTable({
		rows: rows ?? [],
		columns,
		isLoading: categoriesQuery.isLoading || biQuery.isLoading || isLoading,
		getRowId,
	})

	return (
		<div className='flex-auto space-y-3'>
			<h4>{t('widget.sales_factors_impact_by_categories.title')}</h4>

			<div className='flex w-full items-center justify-start gap-2'>
				<div>
					<SRT_TableToolbar table={table} />
				</div>
				<AiDashboardFilter
					filters={params.filters ?? []}
					onChange={handleFilterChange}
				/>
			</div>
			<div className='border-base relative flex h-96 max-h-96 flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
				<MasterTable table={table} />
			</div>
		</div>
	)
}

const columnHelper = createSRTColumnHelper<{
	category: CategoryModel
	metrics: Record<string, number>
}>()

function getColumns({ metrics }: { metrics: string[] }, t: TFunction) {
	return [
		columnHelper.accessor((row) => row?.category?.name || 'n/a', {
			id: 'name',
			header: t('app.category', { defaultValue: 'Category' }),
			meta: {
				label: t('app.category', { defaultValue: 'Category' }),
				placeholder: t('general_search', { defaultValue: 'Search by brand' }),
				variant: 'text',
			},
			Cell: ({ cellValue }) => <div className='flex'>{cellValue}</div>,
		}),
		...metrics.map((metric) =>
			columnHelper.accessor(prop(`metrics.${metric}`), {
				id: metric,
				header: t(`metric.${metric}`),
				Cell: ({ cellValue }) => (
					<div className='flex items-center justify-end font-mono'>
						{cellValue ? formatNumber(cellValue, NumberFormats.Percent) : '-'}
					</div>
				),
				size: 120,
			})
		),
	]
}
